import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { roleLabels } from '../../mocks/stocks';
import { checkPermission, PERMISSIONS } from '../../utils/permissions';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; }

// Plus de PERIODES statiques

export default function PaiePersonnel() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
  });
  const [isAutoPeriod, setIsAutoPeriod] = useState<boolean>(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [paidIds, setPaidIds] = useState<string[]>([]);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'CALCUL' | 'HISTORIQUE'>('CALCUL');
  const [showPayModal, setShowPayModal] = useState<any>(null);
  const [showUndoModal, setShowUndoModal] = useState<any>(null);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payNotes, setPayNotes] = useState<string>('');

  // States for detailed payslip modal
  const [showDetailModal, setShowDetailModal] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailHistory, setDetailHistory] = useState<any[]>([]);
  const [detailActiveTab, setDetailActiveTab] = useState<'VALIDES' | 'EN_COURS' | 'PAIEMENTS'>('VALIDES');

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchData(token, isAutoPeriod ? '' : startDate, endDate);
    } else {
      navigate('/login');
    }
  }, [navigate, startDate, endDate, isAutoPeriod]);

  // Load detailed history when showDetailModal changes
  useEffect(() => {
    if (!showDetailModal) {
      setDetailHistory([]);
      return;
    }
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    const fetchDetailHistory = async () => {
      setDetailLoading(true);
      try {
        const data = await api.getPersonnelHistory(token, showDetailModal.id);
        setDetailHistory(data);
      } catch (err) {
        setToast({ message: 'Erreur lors du chargement des détails', type: 'error' });
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetailHistory();
  }, [showDetailModal]);

  const fetchData = async (token: string, debut: string, fin: string) => {
    setLoading(true);
    try {
      const [statsData, historyData] = await Promise.all([
        api.getPaieStats(token, debut, fin),
        api.getPaieHistory(token)
      ]);
      setPersonnel(statsData);
      setHistory(historyData);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des données', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatDateFr = (dateStr: string) => {
    if (!dateStr) return '-';
    const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr;
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const isTaskInPeriod = (taskDateStr: string, debut: string, fin: string) => {
    if (!taskDateStr) return false;
    const taskDate = taskDateStr.split(' ')[0];
    return taskDate >= debut && taskDate <= fin;
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'TERMINE':
        return <span className="px-2.5 py-1 text-[10px] font-bold bg-green-50 text-green-700 rounded-full border border-green-100">Terminé</span>;
      case 'EN_COURS':
        return <span className="px-2.5 py-1 text-[10px] font-bold bg-blue-50 text-blue-700 rounded-full border border-blue-100">En cours</span>;
      case 'A_AFFECTER':
      case 'EN_ATTENTE':
        return <span className="px-2.5 py-1 text-[10px] font-bold bg-yellow-50 text-yellow-700 rounded-full border border-yellow-100">En attente</span>;
      default:
        return <span className="px-2.5 py-1 text-[10px] font-bold bg-gray-50 text-gray-700 rounded-full border border-gray-100">{status}</span>;
    }
  };

  const handleOpenPayModal = (emp: any) => {
    setShowPayModal(emp);
    setPayAmount(emp.montant);
    setPayNotes('');
  };

  const handleConfirmPay = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !showPayModal) return;

    try {
      setLoading(true);
      await api.savePaieRecord(token, {
        personnel_id: showPayModal.id,
        montant_calcule: showPayModal.montant,
        montant_paye: payAmount,
        notes: payNotes,
        date_debut: showPayModal.date_debut,
        date_fin: showPayModal.date_fin
      });
      setToast({ message: 'Paiement enregistré avec succès !', type: 'success' });
      setShowPayModal(null);
      fetchData(token, isAutoPeriod ? '' : startDate, endDate);
    } catch (err) {
      setToast({ message: 'Erreur lors de l\'enregistrement du paiement', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const totalMasse = personnel.reduce((acc, emp) => {
    const val = (emp.estPaye && emp.montant_paye !== null) ? emp.montant_paye : (emp.montant || 0);
    return acc + val;
  }, 0);

  const handleWhatsAppPaie = async (emp: any) => {
    const phone = emp.telephone?.replace(/\s+/g, '');
    if (!phone) {
        setToast({ message: "Aucun numéro de téléphone pour cet employé", type: 'error' });
        setTimeout(() => setToast(null), 3000);
        return;
    }
    const cleanPhone = phone.startsWith('+') ? phone.replace('+', '') : `223${phone}`;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    setLoading(true);
    try {
      const detailHistoryData = await api.getPersonnelHistory(token, emp.id);
      
      const empValidedTasks = detailHistoryData.filter((t: any) => {
        if (t.statut !== 'TERMINE') return false;
        if (emp.estPaye) {
          return Number(t.paiement_id) === Number(emp.paiement_id);
        } else {
          return !t.paiement_id && isTaskInPeriod(t.updated_at, emp.date_debut, emp.date_fin);
        }
      });

      const groups: { [key: string]: { qty: number, montant: number } } = {};
      empValidedTasks.forEach((t: any) => {
        const typeNom = t.article_nom || t.modele_nom || 'Sur mesure';
        const key = `${typeNom} (${t.tache === 'coupe' ? 'Coupe' : t.tache === 'couture' ? 'Couture' : t.tache})`;
        if (!groups[key]) {
          groups[key] = { qty: 0, montant: 0 };
        }
        groups[key].qty += Number(t.quantite || 1);
        groups[key].montant += Number(t.cout_main_doeuvre || 0);
      });

      const montantReel = emp.estPaye && emp.montant_paye !== null ? emp.montant_paye : emp.montant;
      
      let msg = `*${branding.nom}*\n`;
      msg += `Bonjour ${emp.nom},\n\n`;
      msg += `Voici votre bilan de paie du ${formatDateFr(emp.date_debut)} au ${formatDateFr(emp.date_fin)} 🚀\n\n`;
      
      if (Object.keys(groups).length > 0) {
        msg += `📋 *Détail des pièces traitées :*\n`;
        Object.keys(groups).forEach(key => {
          msg += `• ${key} : ${groups[key].qty} pièce(s) (${formatPrice(groups[key].montant)})\n`;
        });
        msg += `\n`;
      } else {
        msg += `Aucune pièce validée sur cette période.\n\n`;
      }

      msg += `--------------------------\n`;
      msg += `💎 Total pièces : ${emp.nbTotal}\n`;
      msg += `💰 *Total à percevoir : ${formatPrice(montantReel)}*\n\n`;
      msg += `Merci pour votre excellent travail ! ✨`;
      
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    } catch (err) {
      setToast({ message: "Erreur lors de la génération du message WhatsApp", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const validedTasks = showDetailModal ? detailHistory.filter(t => {
    if (t.statut !== 'TERMINE') return false;
    if (showDetailModal.estPaye) {
      return Number(t.paiement_id) === Number(showDetailModal.paiement_id);
    } else {
      return !t.paiement_id && isTaskInPeriod(t.updated_at, showDetailModal.date_debut, showDetailModal.date_fin);
    }
  }) : [];

  const pendingTasks = showDetailModal ? detailHistory.filter(t => t.statut !== 'TERMINE') : [];

  const employeePayments = showDetailModal ? history.filter(p => String(p.personnel_id) === String(showDetailModal.id)) : [];

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Paie du personnel</h2>
            <p className="text-xs text-gray-400 mt-0.5">Calcul automatique et gestion des règlements</p>
          </div>
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button 
              onClick={() => setActiveSection('CALCUL')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeSection === 'CALCUL' ? 'bg-white text-foret-700 shadow-sm' : 'text-gray-500'}`}
            >
              Calcul en cours
            </button>
            <button 
              onClick={() => setActiveSection('HISTORIQUE')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeSection === 'HISTORIQUE' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500'}`}
            >
              Historique des paiements
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {/* Période selector */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-700 text-sm">Mode de calcul de la période</h3>
                <p className="text-xs text-gray-400">
                  {isAutoPeriod 
                    ? "Début automatique après le dernier paiement" 
                    : "Intervalle de dates configuré manuellement"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {/* Toggle Mode */}
                <div className="flex bg-gray-100 p-1 rounded-xl">
                  <button 
                    onClick={() => setIsAutoPeriod(true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isAutoPeriod ? 'bg-white text-foret-700 shadow-sm' : 'text-gray-500'}`}
                  >
                    🔄 Automatique
                  </button>
                  <button 
                    onClick={() => setIsAutoPeriod(false)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${!isAutoPeriod ? 'bg-white text-foret-700 shadow-sm' : 'text-gray-500'}`}
                  >
                    📅 Personnalisé
                  </button>
                </div>

                {/* Date Inputs */}
                <div className="flex items-center gap-3">
                  {!isAutoPeriod && (
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-foret-500">
                      <span className="text-[10px] font-bold text-gray-400 uppercase">Du</span>
                      <input 
                        type="date" 
                        value={startDate} 
                        onChange={e => setStartDate(e.target.value)}
                        className="bg-transparent border-none text-sm font-semibold text-gray-700 outline-none cursor-pointer"
                        title="Date de début"
                        placeholder="YYYY-MM-DD"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-foret-500">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">{isAutoPeriod ? "Jusqu'au" : "Au"}</span>
                    <input 
                      type="date" 
                      value={endDate} 
                      onChange={e => setEndDate(e.target.value)}
                      className="bg-transparent border-none text-sm font-semibold text-gray-700 outline-none cursor-pointer"
                      title="Date de fin"
                      placeholder="YYYY-MM-DD"
                    />
                  </div>
                  <button 
                    onClick={() => {
                      const token = localStorage.getItem('atelier_bassoum_token');
                      if (token) fetchData(token, isAutoPeriod ? '' : startDate, endDate);
                    }}
                    className="w-10 h-10 flex items-center justify-center bg-foret-500 text-white rounded-xl hover:bg-foret-600 transition-all shadow-lg shadow-foret-200 cursor-pointer"
                    title="Rafraîchir les données"
                  >
                    <i className="ri-refresh-line" />
                  </button>
                </div>
              </div>
            </div>
            {isAutoPeriod && (
              <p className="text-[11px] text-blue-600 font-medium mt-3 bg-blue-50/50 px-3 py-2 rounded-lg border border-blue-100/50 inline-block">
                💡 Mode Automatique actif : La date de début est calculée pour chaque employé de manière indépendante (le lendemain de son dernier règlement enregistré).
              </p>
            )}
          </div>

          {activeSection === 'CALCUL' ? (
            <>
              {/* Total */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="col-span-2 bg-foret-600 rounded-2xl p-5 text-white">
                  <p className="text-foret-200 text-xs uppercase tracking-wider mb-1">Masse salariale totale</p>
                  <p className="font-playfair font-bold text-3xl">
                    {loading ? "..." : formatPrice(totalMasse)}
                  </p>
                  <p className="text-foret-200 text-xs mt-2">{personnel.length} employés concernés</p>
                </div>
                <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-2xl p-5 text-blue-800">
                  <p className="text-xs font-medium opacity-80 mb-1">Volume de production</p>
                  <p className="font-bold text-2xl font-playfair">
                    {loading ? "..." : personnel.reduce((s, e) => s + (e.nbTotal || 0), 0)}
                  </p>
                  <p className="text-xs opacity-70 mt-1">tâches terminées (tous ateliers)</p>
                </div>
              </div>

              {/* Employees list */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="font-playfair font-bold text-base text-gray-900">Détail par employé</h3>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-gray-50 pb-20">
                  {personnel.map(emp => {
                    const isPaid = emp.estPaye;
                    const montant = Number(emp.estPaye && emp.montant_paye !== null ? emp.montant_paye : (emp.montant || 0));
                    return (
                      <div key={emp.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                              emp.role === 'coupeur' ? 'bg-blue-400' : 'bg-purple-400'
                            }`}>
                              {emp.nom.split(' ').map((n: any) => n[0]).join('').slice(0,2)}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 leading-tight">{emp.nom}</p>
                              <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mt-0.5">{roleLabels[emp.role] || emp.role}</p>
                              <p className="text-[10px] text-blue-600 font-semibold mt-1">
                                Période : {formatDateFr(emp.date_debut)} au {formatDateFr(emp.date_fin)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black text-foret-700">{formatPrice(montant)}</p>
                            <p className="text-[10px] text-gray-400 font-bold">{emp.nbTotal || 0} tâches</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                           <button
                              onClick={() => handleWhatsAppPaie(emp)}
                              className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 border border-green-100 active:bg-green-100 transition-colors shrink-0"
                              title="Partager sur WhatsApp"
                           >
                              <i className="ri-whatsapp-line" />
                           </button>

                           <button
                              onClick={() => setShowDetailModal(emp)}
                              className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 border border-blue-100 active:bg-blue-100 transition-colors shrink-0"
                              title="Détails du bilan"
                           >
                              <i className="ri-file-list-3-line" />
                           </button>

                           {montant === 0 ? (
                             <div className="flex-1 py-2.5 rounded-xl bg-gray-50 text-gray-400 text-[10px] font-black uppercase text-center border border-gray-100">
                               Aucune pièce
                             </div>
                           ) : isPaid ? (
                             <div className="flex-1 flex items-center gap-2">
                               <div className="flex-1 py-2.5 rounded-xl bg-green-50 text-green-700 text-[10px] font-black uppercase text-center border border-green-100">
                                 ✓ Payé
                               </div>
                               {checkPermission(PERMISSIONS.DELETE_PAIE) && (
                                <button
                                  onClick={() => setShowUndoModal(emp)}
                                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 border border-red-100 active:bg-red-100 shrink-0"
                                  title="Annuler le paiement"
                                >
                                  <i className="ri-arrow-go-back-line" />
                                </button>
                               )}
                             </div>
                           ) : (
                             <button 
                               onClick={() => handleOpenPayModal(emp)}
                               className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-foret-100 active:scale-95 transition-all"
                             >
                               Marquer payé
                             </button>
                           )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Employé</th>
                        <th className="text-center text-xs font-semibold text-gray-500 uppercase px-4 py-3">Période</th>
                        <th className="text-center text-xs font-semibold text-gray-500 uppercase px-4 py-3">Tâches terminées</th>
                        <th className="text-right text-xs font-semibold text-gray-500 uppercase px-4 py-3">Tarif moyen</th>
                        <th className="text-right text-xs font-semibold text-gray-500 uppercase px-4 py-3">Montant</th>
                        <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Statut</th>
                        <th className="text-center text-xs font-semibold text-gray-500 uppercase px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {personnel.map(emp => {
                        const isPaid = emp.estPaye;
                        const montant = Number(emp.montant) || 0;
                        return (
                          <tr key={emp.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                                  emp.role === 'coupeur' ? 'bg-blue-400' : 'bg-purple-400'
                                }`}>
                                  {emp.nom.split(' ').map((n: any) => n[0]).join('').slice(0,2)}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{emp.nom}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    emp.role === 'coupeur' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                                  }`}>
                                    {roleLabels[emp.role] || emp.role}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="text-xs font-semibold text-gray-600 bg-gray-100/70 border border-gray-200/50 px-2.5 py-1.5 rounded-lg inline-block">
                                du {formatDateFr(emp.date_debut)} <br /> au {formatDateFr(emp.date_fin)}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className={`font-bold text-sm ${emp.nbTotal > 0 ? 'text-foret-600' : 'text-gray-300'}`}>
                                {emp.nbTotal}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-sm text-gray-600">{emp.nbTotal > 0 ? formatPrice(montant / emp.nbTotal) : '-'}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <span className="text-base font-bold text-foret-700">
                                {formatPrice(emp.estPaye && emp.montant_paye !== null ? emp.montant_paye : montant)}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-center">
                              {montant === 0 ? (
                                <span className="text-xs text-gray-400 font-medium">Aucune pièce</span>
                              ) : isPaid ? (
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-[10px] bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                    ✓ Payé
                                  </span>
                                  {checkPermission(PERMISSIONS.DELETE_PAIE) && (
                                    <button
                                      onClick={() => setShowUndoModal(emp)}
                                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                                      title="Annuler le paiement"
                                    >
                                      <i className="ri-arrow-go-back-line text-xs" />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleOpenPayModal(emp)}
                                  className="text-[10px] bg-foret-500 hover:bg-foret-600 text-white font-bold px-3 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap uppercase tracking-wider"
                                >
                                Marquer payé
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                    onClick={() => handleWhatsAppPaie(emp)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all cursor-pointer border border-green-100"
                                    title="Envoyer le bilan par WhatsApp"
                                >
                                    <i className="ri-whatsapp-line" />
                                </button>
                                <button
                                    onClick={() => setShowDetailModal(emp)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all cursor-pointer border border-blue-100"
                                    title="Voir la fiche de paie détaillée"
                                >
                                    <i className="ri-file-list-3-line" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-foret-50 border-t-2 border-foret-200">
                        <td colSpan={4} className="px-5 py-3">
                          <span className="font-bold text-gray-900 text-sm">TOTAL MASSE SALARIALE</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-foret-700 text-lg">
                            {formatPrice(totalMasse)}
                          </span>
                        </td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          ) : (
            /* Historique Table */
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between">
                <div>
                  <h3 className="font-playfair font-bold text-xl text-gray-900">Historique des règlements</h3>
                  <p className="text-sm text-gray-400">Tous les paiements effectués</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center">
                  <i className="ri-history-line text-xl" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-8 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                      <th className="px-8 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Employé</th>
                      <th className="px-8 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Période</th>
                      <th className="px-8 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Montant Payé</th>
                      <th className="px-8 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Payé par</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {history.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-5">
                          <p className="text-sm font-bold text-gray-900">{new Date(row.date_paiement).toLocaleDateString('fr-FR')}</p>
                          <p className="text-[10px] text-gray-400 uppercase">{new Date(row.date_paiement).toLocaleTimeString('fr-FR')}</p>
                        </td>
                        <td className="px-8 py-5">
                          <p className="text-sm font-semibold text-gray-700">{row.personnel_nom}</p>
                        </td>
                        <td className="px-8 py-5">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {formatDateFr(row.date_debut)} - {formatDateFr(row.date_fin)}
                          </span>
                        </td>
                        <td className="px-8 py-5 text-right">
                          <p className="text-sm font-bold text-foret-700">{formatPrice(row.montant_paye)}</p>
                          {row.notes && <p className="text-[10px] text-gray-400 italic">"{row.notes}"</p>}
                        </td>
                        <td className="px-8 py-5">
                          <p className="text-xs text-gray-500 font-medium">{row.admin_nom || 'Système'}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Pay Modal */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 rounded-full bg-foret-50 text-foret-600 flex items-center justify-center mb-6 mx-auto">
              <i className="ri-bank-card-line text-3xl" />
            </div>
            <h3 className="font-playfair font-bold text-center text-2xl text-gray-900 mb-2">Valider le paiement</h3>
            <p className="text-sm text-center text-gray-500 mb-8 leading-relaxed">
              Enregistrer le règlement pour **{showPayModal.nom}** sur la période du <strong>{formatDateFr(showPayModal.date_debut)}</strong> au <strong>{formatDateFr(showPayModal.date_fin)}</strong>.
            </p>

            <div className="space-y-4 mb-8">
              <div>
                <label htmlFor="paie-montant-input" className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Montant à payer (FCFA)</label>
                <div className="relative">
                  <input 
                    id="paie-montant-input"
                    type="number"
                    value={payAmount}
                    onChange={e => setPayAmount(Number(e.target.value))}
                    title="Montant du règlement en FCFA"
                    placeholder="0"
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-lg font-bold text-gray-900 focus:ring-2 focus:ring-foret-500 outline-none transition-all"
                  />
                  <div className="absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Ajustable
                  </div>
                </div>
                {payAmount !== showPayModal.montant && (
                  <p className="text-[10px] mt-1.5 text-blue-500 font-bold ml-1 italic">
                    Note: Le calcul initial était de {formatPrice(showPayModal.montant)}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="paie-notes-textarea" className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Notes / Justification</label>
                <textarea 
                  id="paie-notes-textarea"
                  value={payNotes}
                  onChange={e => setPayNotes(e.target.value)}
                  placeholder="Ex: Prime spéciale, retenue pour retard..."
                  title="Commentaire sur le paiement"
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-foret-500 outline-none transition-all h-24 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setShowPayModal(null)}
                className="flex-1 py-4 rounded-2xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={handleConfirmPay}
                className="flex-[2] py-4 rounded-2xl bg-gray-900 text-white font-bold text-xs uppercase tracking-widest hover:bg-black shadow-xl shadow-gray-200 transition-all"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Confirmation Modal */}
      {showUndoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200 text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-6 mx-auto">
              <i className="ri-error-warning-line text-3xl" />
            </div>
            <h3 className="font-playfair font-bold text-2xl text-gray-900 mb-2">Annuler le règlement</h3>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              Voulez-vous vraiment annuler le paiement pour **{showUndoModal.nom}** ? Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowUndoModal(null)}
                className="flex-1 py-4 rounded-2xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600 transition-colors"
              >
                Garder
              </button>
              <button 
                onClick={async () => {
                   const token = localStorage.getItem('atelier_bassoum_token');
                   if (token && showUndoModal.paiement_id) {
                     try {
                       setLoading(true);
                       await api.deletePaieRecord(token, showUndoModal.paiement_id);
                       setToast({ message: 'Règlement annulé avec succès', type: 'success' });
                       fetchData(token, isAutoPeriod ? '' : startDate, endDate);
                     } catch (err: any) {
                       setToast({ message: `Erreur: ${err.message}`, type: 'error' });
                     } finally {
                       setLoading(false);
                       setShowUndoModal(null);
                     }
                   }
                }}
                className="flex-[2] py-4 rounded-2xl bg-red-600 text-white font-bold text-xs uppercase tracking-widest hover:bg-red-700 shadow-xl shadow-red-100 transition-all"
              >
                Oui, Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Payslip Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-black flex-shrink-0 ${
                  showDetailModal.role === 'coupeur' ? 'bg-blue-500 shadow-md shadow-blue-100' : 'bg-purple-500 shadow-md shadow-purple-100'
                }`}>
                  {showDetailModal.nom.split(' ').map((n: any) => n[0]).join('').slice(0,2)}
                </div>
                <div>
                  <h3 className="font-playfair font-bold text-xl text-gray-900 leading-tight">
                    Fiche de paie : {showDetailModal.nom}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      showDetailModal.role === 'coupeur' ? 'bg-blue-550/10 text-blue-700' : 'bg-purple-550/10 text-purple-700'
                    }`}>
                      {roleLabels[showDetailModal.role] || showDetailModal.role}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">•</span>
                    <span className="text-xs text-gray-500 font-bold">
                      Bilan : du {formatDateFr(showDetailModal.date_debut)} au {formatDateFr(showDetailModal.date_fin)}
                    </span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setShowDetailModal(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                title="Fermer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            {/* Metrics cards inside modal */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50/30 border-b border-gray-100 flex-shrink-0">
              <div className="bg-white p-4 rounded-2xl border border-gray-100/80 shadow-sm flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg shrink-0">
                  <i className="ri-check-double-line" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Tâches Validées</p>
                  <p className="font-playfair font-bold text-lg text-gray-900 mt-0.5">
                    {validedTasks.reduce((s, t) => s + Number(t.quantite || 0), 0)} pièces
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-2xl border border-gray-100/80 shadow-sm flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-foret-50 text-foret-600 flex items-center justify-center text-lg shrink-0">
                  <i className="ri-wallet-3-line" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Montant calculé</p>
                  <p className="font-playfair font-bold text-lg text-foret-700 mt-0.5">
                    {formatPrice(showDetailModal.estPaye && showDetailModal.montant_paye !== null ? showDetailModal.montant_paye : showDetailModal.montant)}
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-gray-100/80 shadow-sm flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                  showDetailModal.estPaye ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
                }`}>
                  <i className={showDetailModal.estPaye ? 'ri-checkbox-circle-line' : 'ri-time-line'} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Statut Période</p>
                  <div className="mt-0.5">
                    {showDetailModal.estPaye ? (
                      <span className="text-xs bg-green-100 text-green-700 font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">Payé</span>
                    ) : showDetailModal.montant === 0 ? (
                      <span className="text-xs text-gray-400 font-bold">Aucun gain</span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-700 font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">En attente</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Selector */}
            <div className="flex px-6 border-b border-gray-100 flex-shrink-0 bg-white">
              <button 
                onClick={() => setDetailActiveTab('VALIDES')}
                className={`py-3.5 px-4 font-bold text-xs border-b-2 flex items-center gap-2 transition-all outline-none ${
                  detailActiveTab === 'VALIDES' 
                    ? 'border-foret-600 text-foret-700' 
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <i className="ri-check-double-line" />
                Tâches Validées ({validedTasks.length})
              </button>
              <button 
                onClick={() => setDetailActiveTab('EN_COURS')}
                className={`py-3.5 px-4 font-bold text-xs border-b-2 flex items-center gap-2 transition-all outline-none ${
                  detailActiveTab === 'EN_COURS' 
                    ? 'border-blue-600 text-blue-700' 
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <i className="ri-time-line" />
                Tâches En Cours ({pendingTasks.length})
              </button>
              <button 
                onClick={() => setDetailActiveTab('PAIEMENTS')}
                className={`py-3.5 px-4 font-bold text-xs border-b-2 flex items-center gap-2 transition-all outline-none ${
                  detailActiveTab === 'PAIEMENTS' 
                    ? 'border-purple-600 text-purple-700' 
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <i className="ri-history-line" />
                Historique de Paie ({employeePayments.length})
              </button>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0 bg-gray-50/20">
              {detailLoading ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
                  <div className="w-10 h-10 border-4 border-foret-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs font-bold">Chargement des données...</p>
                </div>
              ) : (
                <>
                  {detailActiveTab === 'VALIDES' && (
                    <div className="space-y-4">
                      {validedTasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                          <i className="ri-inbox-line text-4xl mb-2 opacity-50" />
                          <p className="text-sm font-semibold">Aucune tâche validée sur cette période</p>
                          <p className="text-xs text-gray-400 mt-1">Les tâches terminées dans cet intervalle s'afficheront ici.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase">Validation</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase">Commande</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase">Client</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase">Modèle/Habit</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase">Tâche</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase text-center">Qté</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase text-right">Tarif U.</th>
                                <th className="p-3 text-xs font-bold text-gray-500 uppercase text-right">Montant</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {validedTasks.map(task => {
                                const qte = Number(task.quantite || 1);
                                const totalCost = Number(task.cout_main_doeuvre || 0);
                                const unitCost = totalCost / qte;
                                return (
                                  <tr key={task.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="p-3 text-xs text-gray-500 font-semibold">{formatDateFr(task.updated_at)}</td>
                                    <td className="p-3 text-xs font-bold text-gray-900">{task.commande_code}</td>
                                    <td className="p-3 text-xs font-semibold text-gray-700">{task.client_nom}</td>
                                    <td className="p-3 text-xs text-gray-600">{task.article_nom || task.modele_nom || 'Sur mesure'}</td>
                                    <td className="p-3 text-xs font-bold uppercase tracking-wider text-gray-500 text-[10px]">{task.tache}</td>
                                    <td className="p-3 text-xs text-center font-bold text-gray-900">{qte}</td>
                                    <td className="p-3 text-xs text-right text-gray-600">{formatPrice(unitCost)}</td>
                                    <td className="p-3 text-xs text-right font-bold text-foret-700">{formatPrice(totalCost)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {detailActiveTab === 'EN_COURS' && (
                    <div className="space-y-4">
                      {pendingTasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                          <i className="ri-inbox-line text-4xl mb-2 opacity-50" />
                          <p className="text-sm font-semibold">Aucune tâche en cours</p>
                          <p className="text-xs text-gray-400 mt-1">Cet employé est actuellement libre ou toutes ses tâches sont terminées.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Affectation</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Commande</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Client</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Modèle/Habit</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Tâche</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase text-center">Qté</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase text-center">Statut</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {pendingTasks.map(task => (
                                <tr key={task.id} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="p-3 text-xs text-gray-500 font-semibold">{formatDateFr(task.created_at || task.date_affectation)}</td>
                                  <td className="p-3 text-xs font-bold text-gray-900">{task.commande_code}</td>
                                  <td className="p-3 text-xs font-semibold text-gray-700">{task.client_nom}</td>
                                  <td className="p-3 text-xs text-gray-600">{task.article_nom || task.modele_nom || 'Sur mesure'}</td>
                                  <td className="p-3 text-xs font-bold uppercase tracking-wider text-gray-500 text-[10px]">{task.tache}</td>
                                  <td className="p-3 text-xs text-center font-bold text-gray-900">{task.quantite || 1}</td>
                                  <td className="p-3 text-xs text-center">{renderStatusBadge(task.statut)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {detailActiveTab === 'PAIEMENTS' && (
                    <div className="space-y-4">
                      {employeePayments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                          <i className="ri-history-line text-4xl mb-2 opacity-50" />
                          <p className="text-sm font-semibold">Aucun historique de paiement</p>
                          <p className="text-xs text-gray-400 mt-1">Les règlements enregistrés pour cet employé s'afficheront ici.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Date Paiement</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Période du Bilan</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase text-right">Montant Calculé</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase text-right">Montant Payé</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Payé par</th>
                                <th className="p-3 text-xs font-bold text-gray-400 uppercase">Notes</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {employeePayments.map(pay => (
                                <tr key={pay.id} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="p-3 text-xs text-gray-900 font-bold">
                                    {new Date(pay.date_paiement).toLocaleDateString('fr-FR')}
                                    <span className="block text-[10px] text-gray-400 font-medium">à {new Date(pay.date_paiement).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}</span>
                                  </td>
                                  <td className="p-3 text-xs font-semibold text-gray-600">
                                    du {formatDateFr(pay.date_debut)} au {formatDateFr(pay.date_fin)}
                                  </td>
                                  <td className="p-3 text-xs text-right text-gray-500 font-semibold">{formatPrice(pay.montant_calcule)}</td>
                                  <td className="p-3 text-xs text-right font-black text-foret-700">{formatPrice(pay.montant_paye)}</td>
                                  <td className="p-3 text-xs text-gray-600 font-medium">{pay.admin_nom || 'Système'}</td>
                                  <td className="p-3 text-xs text-gray-400 italic font-medium max-w-[200px] truncate" title={pay.notes}>
                                    {pay.notes ? `"${pay.notes}"` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
              <button
                onClick={() => handleWhatsAppPaie(showDetailModal)}
                className="py-3 px-5 rounded-2xl bg-green-50 text-green-600 font-bold text-xs uppercase tracking-widest hover:bg-green-600 hover:text-white transition-all flex items-center gap-2 border border-green-100"
              >
                <i className="ri-whatsapp-line text-sm" />
                Partager
              </button>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDetailModal(null)}
                  className="py-3 px-5 rounded-2xl text-gray-500 hover:bg-gray-50 font-bold text-xs uppercase tracking-widest transition-colors border border-gray-100"
                >
                  Fermer
                </button>
                
                {!showDetailModal.estPaye && showDetailModal.montant > 0 && (
                  <button 
                    onClick={() => {
                      setShowDetailModal(null);
                      handleOpenPayModal(showDetailModal);
                    }}
                    className="py-3 px-5 rounded-2xl bg-foret-500 text-white font-bold text-xs uppercase tracking-widest hover:bg-foret-600 transition-colors shadow-lg shadow-foret-100"
                  >
                    Marquer payé
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

